import React, { createContext, useContext, useEffect, useState } from "react";

const ConfigContext = createContext();

// Default config fallback
const DEFAULT_CONFIG = { apiBaseUrl: "http://localhost:8000/api" };

// Global config cache for non-React usage
let globalConfig = DEFAULT_CONFIG;
let configPromise = null;

// Function to get config without hooks (for service files)
export function getConfig() {
  return globalConfig;
}

// Function to get config with promise (for async initialization)
export function getConfigAsync() {
  if (!configPromise) {
    initializeConfig();
  }
  return configPromise;
}

// Initialize config immediately and update global config when ready
initializeConfig().then((config) => {
  globalConfig = config;
});

// Function to initialize config (called by ConfigProvider)
export function initializeConfig() {
  if (configPromise) {
    return configPromise;
  }

  // actual current host
  const configURL = `${window.location.protocol}//${window.location.host}/config/`;

  configPromise = fetch(configURL)
    .then((res) => {
      if (!res.ok) throw new Error("Failed to fetch config");
      return res.json();
    })
    .then((data) => {
      globalConfig = data;
      return data;
    })
    .catch((err) => {
      globalConfig = DEFAULT_CONFIG;
      return DEFAULT_CONFIG;
    });

  return configPromise;
}

// Re-reads the served config, bypassing the one-per-page-load cache. A tab
// opened before a deploy keeps the apiBaseUrl it loaded with; if the stack
// has since moved the API, that host may no longer resolve. Resolves to the
// fresh apiBaseUrl, or null when the config cannot be fetched.
export function fetchServedApiBaseUrl() {
  const configURL = `${window.location.protocol}//${window.location.host}/config/`;
  try {
    return fetch(configURL, { cache: "no-store" })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!data || typeof data.apiBaseUrl !== "string") return null;
        globalConfig = data;
        return data.apiBaseUrl;
      })
      .catch(() => null);
  } catch {
    return Promise.resolve(null);
  }
}

export function ConfigProvider({ children }) {
  const [config, setConfig] = useState(globalConfig);
  const [error, setError] = useState(null);

  useEffect(() => {
    initializeConfig()
      .then((data) => {
        setConfig(data);
        setError(null);
      })
      .catch((err) => {
        setError(err.message);
        setConfig(DEFAULT_CONFIG);
      });
  }, []);

  return (
    <ConfigContext.Provider value={{ config, error }}>
      {children}
    </ConfigContext.Provider>
  );
}

export function useConfig() {
  const context = useContext(ConfigContext);
  if (context === undefined) {
    throw new Error("useConfig must be used within a ConfigProvider");
  }
  return context;
}

export default ConfigContext;
